import React from 'react';
import { Grid } from '@mui/material';
import { 
  TransactionDrillDownData, 
  DrillDownEvolucaoComprasData, 
  DrillDownEvolucaoVendasData,
  PartnerData,
  ProductData,
  CfopData,
  DailyEvolutionData,
  PeriodComparison,
  PeriodData,
  FornecedorData,
  ClienteData
} from './types';
import { getDrillDownConfig } from './configs';
import DrillDownDailyEvolutionChart from './DailyEvolutionChart';
import DrillDownPeriodSummary from './DrillDownPeriodSummary';
import DrillDownTopSuppliersChart from './DrillDownTopSuppliersChart';
import DrillDownTopCustomersChart from './DrillDownTopCustomersChart';
import DrillDownCfopPeriodoChart from './DrillDownCfopPeriodoChart';
import DrillDownProductsSection from './DrillDownProductsSection';

interface GenericTransactionDrillDownProps {
  data: TransactionDrillDownData | DrillDownEvolucaoComprasData | DrillDownEvolucaoVendasData;
  transactionType: 'compras' | 'vendas';
}

const GenericTransactionDrillDown: React.FC<GenericTransactionDrillDownProps> = ({ 
  data, 
  transactionType 
}) => {
  const config = getDrillDownConfig(transactionType);
  
  // Função para normalizar os dados para o formato genérico
  const normalizeData = (
    rawData: TransactionDrillDownData | DrillDownEvolucaoComprasData | DrillDownEvolucaoVendasData
  ) => {
    // Tentar múltiplas fontes de dados para compatibilidade
    let parceiros_periodo;
    
    if (transactionType === 'compras') {
      parceiros_periodo = (rawData as DrillDownEvolucaoComprasData).fornecedores_periodo ||
                         (rawData as any).parceiros_periodo;
    } else {
      parceiros_periodo = (rawData as DrillDownEvolucaoVendasData).clientes_periodo ||
                         (rawData as any).parceiros_periodo;
    }

    // Normaliza dados específicos para formato genérico
    const normalizedData = {
      periodo: rawData.periodo,
      cfops_periodo: rawData.cfops_periodo,
      evolucao_diaria: rawData.evolucao_diaria,
      comparativo_periodo: rawData.comparativo_periodo,
      produtos_periodo: rawData.produtos_periodo,
      parceiros_periodo,
      transactionType
    };

    return normalizedData;
  };

  const normalizedData = normalizeData(data);
  
  const { 
    periodo, 
    parceiros_periodo, 
    produtos_periodo, 
    cfops_periodo, 
    evolucao_diaria, 
    comparativo_periodo 
  } = normalizedData;

  if (!periodo) {
    return null;
  }

  // Dados de parceiros já prontos para o componente
  const partnersData = parceiros_periodo || [];
  
  // Função para obter o valor correto baseado no tipo de transação
  const getTransactionValue = (item: any) => {
    return transactionType === 'compras' ? item.valor_compras : item.valor_vendas;
  };

  return (
    <Grid container spacing={3}>
      {/* Informações do Período */}
      <Grid item xs={12}>
        <DrillDownPeriodSummary 
          periodo={periodo}
          comparativo_periodo={comparativo_periodo}
          transactionType={transactionType}
          config={config}
        />
      </Grid>

      {/* Gráfico de Evolução Diária */}
      <DrillDownDailyEvolutionChart
        evolucao_diaria={evolucao_diaria || []}
        title={config.dailyEvolutionTitle}
        valueKey={config.valueKeys.dailyValue as keyof DailyEvolutionData}
        valueLabel={config.valueLabel}
        color={config.colors.primary}
      />

      {/* Gráfico de Top Parceiros */}
      {transactionType === 'compras' ? (
        <DrillDownTopSuppliersChart 
          fornecedores={partnersData as FornecedorData[]}
          title={config.chartTitle}
        />
      ) : (
        <DrillDownTopCustomersChart 
          clientes={partnersData as ClienteData[]}
          title={config.chartTitle}
        />
      )}

      {/* Gráfico de CFOPs */}
      <DrillDownCfopPeriodoChart
        cfops_periodo={cfops_periodo || []}
        title="Principais CFOPs do Período"
        maxItems={8}
        gridSize={{ xs: 12, md: 12 }}
        height={400}
      />

      {/* Tabela de Produtos */}
      <DrillDownProductsSection
        produtos={produtos_periodo || []}
        title={config.productsSectionTitle}
        showPagination={true}
        rowsPerPage={5}
        maxHeight="500px"
        gridSize={{ xs: 12 }}
        transactionType={transactionType}
        config={config}
      />
    </Grid>
  );
};

export default GenericTransactionDrillDown; 